'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  useRouter,
} from 'next/navigation';
import {
  AuthState,
  logout as logoutUser,
  getAuthToken,
  getSessionHash,
  clearStoredAuth,
  getPatientOrgMappings,
} from '@/utils/auth';
import {
  setGlobalLogout,
  setAuthHeaders,
  resetRefreshAttempts,
} from '@/utils/httpClient';
import {
  PatientOrgMappingDTO,
} from '@/dtos';
import {
  useDatadog,
  useGoogleMaps,
} from '@/hooks';
import {
  useAuthLogout,
} from '@/hooks/useApi';
import {
  SignalRHubContext,
} from '@/core/context/signalRHub';
import {
  isEmptyString,
} from '@/utils/misc';
import {
  datadogLogs,
} from '@datadog/browser-logs';
import isNil from 'lodash/isNil';

export type LogoutOptions = {
  redirect?: string;
};

type AuthContextType = AuthState & {
  logout: (options?: LogoutOptions) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { disconnect: disconnectSignalR } = useContext(SignalRHubContext);
  const { mutateAsync: callLogoutAPI } = useAuthLogout();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const router = useRouter();

  // Load Google Maps script once when the app initializes
  useGoogleMaps(state.isAuthenticated);

  const checkAuth = async() => {
    if (isLoggingOut) {
      console.log('Skipping auth check during logout');
      return;
    }

    const token = getAuthToken();
    const patientOrgMappings = getPatientOrgMappings() as PatientOrgMappingDTO;

    // If we have valid token and patient org mappings, user is authenticated
    if(!isEmptyString(token) && patientOrgMappings?.organizationHash && patientOrgMappings?.patientHash) {
      // Set auth headers for API calls
      setAuthHeaders(token ?? '', patientOrgMappings?.organizationHash ?? '', patientOrgMappings?.patientHash ?? '');

      // Reset refresh attempts on successful authentication
      resetRefreshAttempts();

      setState({
        user: patientOrgMappings,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } else {
      // No valid auth data found, clear everything
      clearStoredAuth();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [isLoggingOut]);

  const { setDatadogUser } = useDatadog();
  useEffect(() => {
    if (!isNil(state.user)) {
      setDatadogUser(state.user);
    }
  }, [state?.user, setDatadogUser]);

  const logout = async (options?: LogoutOptions) => {
    setIsLoggingOut(true); // Set flag to prevent auth check during logout
    setState(prev => ({ ...prev, isLoading: true }));

    const path = options?.redirect;
    const loginUrl = path && !isEmptyString(path)
      ? `/login?redirect=${encodeURIComponent(path)}`
      : '/login';

    try {
      // Get session hash before clearing auth data
      const sessionHash = getSessionHash();

      // Call logout API with session hash
      if (sessionHash) {
        console.log('Calling logout API with session hash');
        try {
          await callLogoutAPI({ sessionHash });
          console.log('Logout API call successful');
        } catch (apiError) {
          console.error('Logout API error (continuing with local logout):', apiError);
          // Continue with local logout even if API call fails
        }
      }

      // Clear auth state first to prevent race conditions
      console.log('Clearing auth state');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      // Disconnect SignalR connection
      await disconnectSignalR();
      console.log('SignalR disconnected during logout');

      // Stop datadog session
      datadogLogs.clearUser();

      // Clear stored auth data
      console.log('Clearing stored auth data');
      clearStoredAuth();

      // Reset refresh attempts on logout
      resetRefreshAttempts();

      // Call legacy logout function (if needed)
      console.log('Calling legacy logout');
      await logoutUser();

      console.log('Logout successful, redirecting to login');
      // Force redirect to login and replace current history
      // Use a small delay to ensure state is properly updated
      setTimeout(() => {
        // Clear any remaining auth data and redirect
        clearStoredAuth();
        router.replace(loginUrl);
        setIsLoggingOut(false); // Reset flag after logout

        // Final verification that auth is cleared
        setTimeout(() => {
          const token = getAuthToken();
          console.log('Final auth check after logout:', {
            token: token ? 'exists' : 'cleared',
          });
        }, 100);
      }, 50);
    } catch (error) {
      console.error('Logout error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to logout',
      }));
      setIsLoggingOut(false); // Reset flag on error
    }
  };

  // Register logout function with HTTP client for global 401 handling
  useEffect(() => {
    setGlobalLogout(() => {
      console.log('HTTP Client: Global logout triggered by 401 error');
      logout();
    });
  }, [logout]);

  const value: AuthContextType = {
    ...state,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Logout Button Component
export const LogoutButton: React.FC<{ className?: string; children?: ReactNode }> = ({
  className = '',
  children = 'Logout',
}) => {
  const { logout, isLoading } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <button
      onClick={handleLogout}
      disabled={isLoading}
      className={`px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 ${className}`}
    >
      {isLoading ? 'Logging out...' : children}
    </button>
  );
};